import { LitElement, TemplateResult, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import classNames from 'classnames';
import { getGlobalStyleSheets } from '../../global-styles';
import { AtlDropDownMenuProps } from './props.ts';

import * as TagNames from './dropdownmenu.tagnames.ts';
import { DropDownMenuClickEvent } from './dropdownmenu.events.ts';

@customElement(TagNames.DropDownMenu)
export class AtlDropDownMenu
   extends LitElement
   implements AtlDropDownMenuProps
{
   declare containerId: string;
   declare containerClassName: string;
   declare refId: string;
   declare applyLeft?: boolean;
   declare protected toggled: boolean;
   private _isWindowClickListenerAttached: boolean;

   static properties = {
      containerId: { type: String },
      containerClassName: { type: String },
      refId: { type: String },
      applyLeft: { type: Boolean },
      toggled: { type: Boolean, state: true },
   };

   constructor() {
      super();
      this.containerId = '';
      this.containerClassName = '';
      this.refId = '';
      this.toggled = false;
      this._isWindowClickListenerAttached = false;
   }

   disconnectedCallback() {
      this._removeWindowClickListener();
      super.disconnectedCallback();
   }

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .dropdown-menu {
            margin-top: 0px !important;
         }
      `,
   ];

   dropDownContent(list?: TemplateResult) {
      return html`
         <div
            id=${this.containerId}
            class=${classNames('dropdown', this.containerClassName)}
            @click=${() => this._handleToggle()}
         >
            <span
               id=${this._menuButtonId}
               class="d-inline-block"
               data-bs-toggle="dropdown"
               data-type="user-profile-menu-toggle"
               style="display: block; width: fit-content;"
            >
               <slot name="slotMenuIcon"></slot>
            </span>
            ${list}
         </div>
      `;
   }

   dropDownBody() {
      return html`
         <ul
            id=${this._menuListId}
            class=${classNames(
               'dropdown-menu py-0 border border-secondary rounded',
               { show: this.toggled, 'dropdown-menu-end': this.applyLeft }
            )}
            style=${!this.applyLeft && 'right: auto;'}
            data-bs-popper="none"
            data-type="user-profile-menu-dropdown"
            role="menu"
            aria-labelledby=${this._menuButtonId}
         >
            <slot name="slotMenuContent"></slot>
         </ul>
      `;
   }

   render() {
      if (this.toggled === true) {
         return html`${this.dropDownContent(this.dropDownBody())}`;
      } else {
         return html`${this.dropDownContent()}`;
      }
   }

   protected _handleToggle = () => {
      this.toggled = !this.toggled;
      this._dispatchMenuClickEvent(this.toggled);

      if (this.toggled) {
         this._addWindowClickListener();
      } else {
         this._removeWindowClickListener();
      }
   };

   private _dispatchMenuClickEvent = (payload: boolean) => {
      window.dispatchEvent(new DropDownMenuClickEvent(this.refId, payload));
   };

   private _addWindowClickListener() {
      if (this._isWindowClickListenerAttached) {
         return;
      }

      window.addEventListener('click', this._onWindowClick, true);
      this._isWindowClickListenerAttached = true;
   }

   private _removeWindowClickListener() {
      if (!this._isWindowClickListenerAttached) {
         return;
      }

      window.removeEventListener('click', this._onWindowClick, true);
      this._isWindowClickListenerAttached = false;
   }

   private _onWindowClick = (event: MouseEvent) => {
      if (!this.toggled) {
         this._removeWindowClickListener();
         return;
      }

      const eventPath = event.composedPath();
      if (eventPath.includes(this)) {
         return;
      }

      this.toggled = false;
      this._dispatchMenuClickEvent(this.toggled);
      this._removeWindowClickListener();
   };

   private get _menuButtonId() {
      return this.refId
         ? `${this.refId}-menu-button`
         : 'atl-drop-down-menu-cont';
   }

   private get _menuListId() {
      return this.refId ? `${this.refId}-menu-list` : 'atl-drop-down-menu-list';
   }
}
